/**
 * Iter153 — Student spider chart + progress timeline.
 * Renders the current skill snapshot as a simple SVG spider, plus a
 * "View My Progress Timeline" button that lists every past snapshot with
 * its session summary. No AI label per spec.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarClock, Printer } from "lucide-react";

function Spider({ snapshot = {}, size = 220, color = "#CCFF00" }) {
  const skills = Object.keys(snapshot).slice(0, 8);
  if (skills.length < 3) {
    return (
      <div
        style={{ width: size, height: size }}
        className="flex items-center justify-center text-[11px] text-[#525252] border border-[#262626] rounded-full"
        data-testid="student-spider-empty"
      >
        No snapshot yet
      </div>
    );
  }
  const cx = size / 2, cy = size / 2, r = size / 2 - 22;
  const step = (Math.PI * 2) / skills.length;
  const points = skills.map((k, i) => {
    const val = Math.max(0, Math.min(5, Number(snapshot[k]) || 0)) / 5;
    const a = -Math.PI / 2 + i * step;
    return [cx + Math.cos(a) * r * val, cy + Math.sin(a) * r * val];
  });
  const rings = [0.25, 0.5, 0.75, 1];
  const poly = points.map((p) => p.join(",")).join(" ");
  return (
    <svg width={size} height={size} data-testid="student-spider-chart">
      {rings.map((ring, i) => (
        <polygon key={i} points={skills.map((_, j) => {
          const a = -Math.PI / 2 + j * step;
          return `${cx + Math.cos(a) * r * ring},${cy + Math.sin(a) * r * ring}`;
        }).join(" ")} fill="none" stroke="#262626" strokeWidth="1" />
      ))}
      {skills.map((k, i) => {
        const a = -Math.PI / 2 + i * step;
        const lx = cx + Math.cos(a) * (r + 12);
        const ly = cy + Math.sin(a) * (r + 12);
        return (
          <text key={k} x={lx} y={ly} fontSize="9" fill="#A3A3A3"
            textAnchor="middle" dominantBaseline="middle" style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {k.replace(/_/g, " ")}
          </text>
        );
      })}
      <polygon points={poly} fill={color + "33"} stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

export default function StudentSpiderTimeline({ studentId }) {
  const [entries, setEntries] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    api.get(`/students/${studentId}/skill-snapshots`)
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => { setEntries([]); toast.error("Couldn't load skill snapshots"); });
  }, [studentId]);

  // Iter154 — Print/Save-as-PDF. We flip a body class that a scoped print
  // stylesheet uses to isolate the timeline card and blow away nav chrome,
  // then call window.print(). Browsers universally offer "Save as PDF" in
  // that dialog — no jsPDF dependency required.
  const printPdf = () => {
    if (!entries || entries.length === 0) {
      toast.info("Nothing to print yet — your timeline is empty.");
      return;
    }
    setShowTimeline(true);           // ensure DOM is populated
    setPrinting(true);
    document.body.classList.add("rl-print-timeline");
    // Give React one frame to expand the timeline before the print dialog.
    setTimeout(() => {
      window.print();
      document.body.classList.remove("rl-print-timeline");
      setPrinting(false);
    }, 250);
  };

  if (entries === null) return null;
  const current = entries[0]?.snapshot || {};

  return (
    <div className="card-surface p-6 space-y-4 rl-print-root" data-testid="student-spider-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-mono text-[#CCFF00]">SKILL SNAPSHOT</div>
          <div className="text-sm text-[#A3A3A3]">
            {entries.length ? `Latest ${entries.length === 1 ? "session" : `of ${entries.length} sessions`}` : "No sessions logged yet"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!entries.length}
            onClick={() => setShowTimeline((s) => !s)}
            data-testid="view-progress-timeline"
            className="min-h-[44px] px-4 py-2 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#CCFF00] rl-no-print"
          >
            {showTimeline ? "Hide Timeline" : "View My Progress Timeline"}
          </button>
          <button
            type="button"
            disabled={!entries.length || printing}
            onClick={printPdf}
            data-testid="download-timeline-pdf"
            title="Print or save as PDF"
            className="min-h-[44px] px-4 py-2 text-xs uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#66C2FF] inline-flex items-center gap-1.5 rl-no-print"
          >
            <Printer className="w-3.5 h-3.5" strokeWidth={2} />
            {printing ? "Preparing…" : "Save as PDF"}
          </button>
        </div>
      </div>

      <div className="flex justify-center">
        <Spider snapshot={current} />
      </div>

      {showTimeline && (
        <div className="space-y-3 pt-3 border-t border-[#262626]" data-testid="progress-timeline">
          {entries.map((e) => (
            <div key={e.feedback_id} className="border border-[#262626] p-4 space-y-3" data-testid={`timeline-entry-${e.feedback_id}`}>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                  {(e.session_start || e.submitted_at || "").slice(0, 10)} · {e.session_title || "Session"}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#525252]">
                  Logged {(e.submitted_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
              {e.summary && <div className="text-sm text-[#E5E5E5] leading-relaxed">{e.summary}</div>}
              {(e.focus_areas || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {e.focus_areas.map((f) => (
                    <span key={f} className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#FF9F1C] text-[#FF9F1C]">{f}</span>
                  ))}
                </div>
              )}
              <div className="flex justify-center">
                <Spider snapshot={e.snapshot} size={180} color="#66C2FF" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
